import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva } from "class-variance-authority";

import { cn } from "@/lib/utils"
import { useConnection } from "@/contexts/ConnectionContext"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default:
          "bg-[#005EF8] text-white shadow hover:bg-[#0047C2]",
        destructive:
          "bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90",
        outline:
          "border border-[#D3D5D4] bg-white text-[#323033] shadow-sm hover:bg-[#F5F5F5] hover:text-[#323033]",
        secondary:
          "bg-[#323033] text-white shadow-sm hover:bg-[#17161B]",
        ghost: "text-[#323033] hover:bg-[#F5F5F5] hover:text-[#323033]",
        link: "text-[#005EF8] underline-offset-4 hover:underline",
      },
      size: {
        default: "h-9 px-4 py-2",
        sm: "h-8 rounded px-3 text-xs",
        lg: "h-10 rounded px-8",
        icon: "h-9 w-9",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

/**
 * Returns true if this button should be auto-disabled while offline.
 *
 * Triggers when ANY of the following is true:
 *  • `type="submit"` (universal form-submission hook — covers Save/Submit
 *    flows that wrap content in a <form>)
 *  • `data-mutating="true"` (explicit opt-in for click-handler-driven
 *    Save/Update/Create buttons that don't sit inside a form)
 *
 * The legacy `asChild` path (Radix Slot) is intentionally NOT gated — Slot
 * forwards into arbitrary children (often <a> links), and silently disabling
 * navigation links would violate the "read-only navigation must keep working"
 * guardrail.
 */
const _shouldOfflineGate = (props) =>
  !props.asChild && (props.type === "submit" || props["data-mutating"] === "true" || props["data-mutating"] === true);

const Button = React.forwardRef(({ className, variant, size, asChild = false, ...props }, ref) => {
  const Comp = asChild ? Slot : "button"
  const { isOnline } = useConnection()
  const offlineGated = !isOnline && _shouldOfflineGate({ asChild, ...props })

  // Compose disabled + title without stomping on caller-provided values when
  // we're online. When offline, the offline state wins so the user knows why
  // the button isn't responsive.
  const finalDisabled = offlineGated || props.disabled
  const finalTitle = offlineGated ? "You are offline. Reconnect to save." : props.title

  return (
    <Comp
      className={cn(buttonVariants({ variant, size, className }))}
      ref={ref}
      {...props}
      disabled={finalDisabled}
      title={finalTitle}
      aria-disabled={finalDisabled || undefined}
      data-offline-gated={offlineGated || undefined}
    />
  );
})
Button.displayName = "Button"

export { Button, buttonVariants }
